import { computed, inject, Injectable, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router } from "@angular/router";
import { Observable, filter, fromEvent, map, startWith } from "rxjs";

import { getRootFontSizePx, pxToRem } from "../shared";

import { resolveArrowStep, resolveDragFromClosed, resolveDragFromOpen } from "./side-nav-resize";
import { SIDE_NAV_WIDTH_BOUNDS, SideNavWidthService } from "./side-nav-width.service";

export type SideNavVersion = "default" | "vfo1";

/**
 * The side nav state machine: open/closed, push/overlay, transition gating, and what a resize
 * gesture means.
 *
 * Width itself lives in `SideNavWidthService` and the gesture arithmetic in `side-nav-resize.ts`.
 * This class never touches disk — it only picks a verb: `display` to paint a width, `commit` to
 * make it the user's preference.
 */
@Injectable({
  providedIn: "root",
})
export class SideNavService {
  private rootFontSizePx: number;

  readonly version = signal<SideNavVersion>("default");

  /**
   * Whether the side navigation is open or closed.
   */
  readonly open = signal(false);

  /**
   * Whether the nav is in push mode (occupies its own grid column).
   * Set by LayoutComponent via ResizeObserver.
   */
  readonly isPushMode = signal(false);

  /**
   * True when the nav is open but not in push mode — it overlays the content.
   */
  readonly isOverlay = computed(() => this.open() && !this.isPushMode());

  /**
   * Explicit user preference for open/closed state, set when the user manually
   * toggles the nav. Null means no preference (auto-open when push mode allows).
   */
  readonly userCollapsePreference = signal<"open" | "closed" | null>(null);

  /** True while the user is actively dragging the resize handle. Disables CSS transitions during drag. */
  readonly isDragging = signal(false);

  /**
   * True once the browser has painted the layout's first measurement, so the initial open/width
   * state is on screen before transitions turn on and the nav does not animate in on page load.
   */
  private readonly layoutReady = signal(false);

  /** True once the initial width and layout have settled, so width changes may animate. */
  readonly transitionsEnabled = computed(() => this.widthService.hydrated() && this.layoutReady());

  /**
   * Visual width override (in rem) applied during a drag via a direct style binding: the preview
   * below the minimum width when dragging out from collapsed, and the tension shrink when an open
   * nav is dragged toward the snap threshold. Drives width alone — the nav keeps its closed styling
   * until it actually opens. Never persisted. Null when no drag is in progress.
   */
  readonly dragDisplayWidth = signal<number | null>(null);

  /** Owns the width and decides what is persisted. This service never writes to disk itself. */
  private readonly widthService = inject(SideNavWidthService);

  private readonly router = inject(Router);

  /** True once the saved width has been read from disk, so callers can tell startup from a resize. */
  readonly widthHydrated = this.widthService.hydrated;

  private readonly _widthResizedByUser = signal(false);

  /** True once the user has resized the nav themselves, which ends startup on its own. */
  readonly widthResizedByUser = this._widthResizedByUser.asReadonly();

  /** Current nav width, in rem. */
  readonly widthRem = this.widthService.width;

  constructor() {
    // Get computed root font size to support user-defined a11y font increases
    this.rootFontSizePx = getRootFontSizePx();

    // vfo1 top-level groups have no route, so a navigation means the user picked a page.
    // Some layouts render the nav only after navigation ends, so the component would miss it.
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        if (this.version() === "vfo1" && this.isOverlay()) {
          this.open.set(false);
        }
      });
  }

  /**
   * Called by LayoutComponent from its first ResizeObserver callback. That callback runs before the
   * browser paints the frame, and so do `afterNextRender` and `requestAnimationFrame` — arming the
   * width transition in any of them means arming it in the same frame that first paints the width,
   * and the nav animates in on page load. `setTimeout` is the next macrotask, i.e. after the paint.
   */
  armTransitionsAfterFirstPaint() {
    if (!this.layoutReady()) {
      setTimeout(() => this.layoutReady.set(true));
    }
  }

  /** Toggle the open/close state of the side nav. */
  toggle() {
    if (this.open()) {
      this.userCollapsePreference.set("closed");
      this.open.set(false);
      return;
    }

    this._expand();
  }

  /**
   * Set new side nav width from drag event coordinates.
   *
   * @param eventXPointer x coordinate of the pointer
   * @param dragElementXCoordinate x coordinate of the drag element's bounding client rect
   */
  setWidthFromDrag(eventXPointer: number, dragElementXCoordinate: number) {
    this.isDragging.set(true);
    this._widthResizedByUser.set(true);

    const newWidthInRem = pxToRem(eventXPointer - dragElementXCoordinate, this.rootFontSizePx);

    if (!this.open()) {
      // Dragging out from collapsed — a preview drives the visual width without changing `open`,
      // so push/overlay mode and open-state styling stay put until the nav actually opens.
      const step = resolveDragFromClosed(newWidthInRem);

      if (step.action === "preview") {
        this.dragDisplayWidth.set(step.width);
        return;
      }

      this.dragDisplayWidth.set(null);

      if (step.action === "open") {
        // The width hands off to the width service now that the nav is genuinely open.
        this.userCollapsePreference.set("open");
        this.open.set(true);
        this.widthService.display(this.widthService.clamp(step.width));
      }
      return;
    }

    const step = resolveDragFromOpen(newWidthInRem);

    if (step.action === "tension") {
      this.dragDisplayWidth.set(step.width);
      return;
    }

    this.dragDisplayWidth.set(null);

    if (step.action === "collapse") {
      this.userCollapsePreference.set("closed");
      this.open.set(false);
      // Discard the widths this drag painted on the way down and go back to the user's.
      this.widthService.display(this.widthService.saved());
      return;
    }

    this.widthService.display(this.widthService.clamp(step.width));
  }

  /**
   * Set new side nav width from arrow key events.
   *
   * @param key event key, must be either ArrowRight or ArrowLeft
   */
  setWidthFromKeys(key: "ArrowRight" | "ArrowLeft") {
    this._widthResizedByUser.set(true);

    const step = resolveArrowStep(key, this.widthRem(), this.open());

    switch (step.action) {
      case "expand":
        this._expand();
        return;
      case "collapse":
        this.userCollapsePreference.set("closed");
        this.open.set(false);
        return;
      case "commit":
        this.widthService.commit(step.width);
        return;
      case "noop":
        return;
    }
  }

  /** A drag only ever paints. Release is the single place it becomes a preference, so a gesture
   *  that ends collapsed cannot overwrite the width the user had. */
  onDragEnd() {
    this.isDragging.set(false);

    const preview = this.dragDisplayWidth();
    this.dragDisplayWidth.set(null);

    if (!this.open()) {
      // Released in the collapsed preview zone — open at the width the user already had.
      // Otherwise the drag snapped closed, which says nothing about the width.
      if (preview !== null) {
        this._expand();
      }
      return;
    }

    if (preview !== null) {
      // Released in the tension zone — spring back to the minimum.
      this.widthService.commit(SIDE_NAV_WIDTH_BOUNDS.min);
      return;
    }

    // Released while open — the painted width is the width the user chose.
    this.widthService.commit(this.widthRem());
  }

  /** Open at the width the user last chose. Display-only, so restoring it is not a new
   *  preference and a later collapse still returns to the same width. */
  private _expand() {
    this.userCollapsePreference.set("open");
    this.open.set(true);
    this.widthService.display(this.widthService.saved());
  }
}

/** Emits whether `query` matches, starting with its current value. */
export const media = (query: string): Observable<boolean> => {
  const mediaQuery = window.matchMedia(query);
  return fromEvent<MediaQueryList>(mediaQuery, "change").pipe(
    startWith(mediaQuery),
    map((list: MediaQueryList) => list.matches),
  );
};
